import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showModal } from '/shared/state/actions/modal';

import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import getUserNotificationsSelector from '/business/shared/state/selectors/auth/getUserNotificationsSelector';
import isAuthedSelector from '/business/shared/state/selectors/auth/isAuthedSelector';
import type { AppDispatch } from '/business/shared/state/store';

type UserNotification = {
  applicationId?: number | null;
  createdAt?: string;
  id?: number | string | null;
};

const getCreatedTime = (notification: UserNotification): number => {
  if (!notification.createdAt) {
    return 0;
  }
  return new Date(notification.createdAt).getTime();
};

const UserNotifications = () => {
  const dispatch = useDispatch<AppDispatch>();
  const applicationId = useSelector(getApplicationIdSelector) || null;
  const notifications = useSelector(getUserNotificationsSelector);
  const isAuthed = useSelector(isAuthedSelector) || false;

  const [activeUserNotification, setActiveUserNotification] = useState<UserNotification | null>(null);

  const latestNotification = useMemo(() => {
    if (!isAuthed) {
      return null;
    }
    const filteredNotifications = notifications
      .filter((notification) => {
        return notification.applicationId === applicationId;
      })
      .sort((a, b) => {
        return getCreatedTime(b) - getCreatedTime(a);
      });
    return filteredNotifications.length > 0 ? filteredNotifications[0] : null;
  }, [applicationId, notifications, isAuthed]);

  useEffect(() => {
    if (latestNotification) {
      setActiveUserNotification(latestNotification);
      dispatch(
        showModal({
          name: 'CMP_USER_NOTIFICATION',
          isBlocking: true,
          metadata: {
            notification: latestNotification,
            setActiveUserNotification,
          },
        }),
      );
    } else {
      setActiveUserNotification(null);
    }
  }, [latestNotification]);

  if (!isAuthed) {
    return null;
  }

  return null;
};

export default UserNotifications;
